/**
 * Recupero dai 404 sui chunk lazy-loaded.
 *
 * Perché serve: ogni replica della Container App serve i propri /assets/*.js.
 * Durante il rollover di un deploy — e, finché le revision non sono pinnate al
 * digest, anche durante un normale scale-out — il browser può caricare
 * index.html da una replica e chiedere un chunk a un'altra, che quel file non
 * ce l'ha. Vite emette `vite:preloadError`; senza handler l'errore propaga
 * oltre <Suspense> e la pagina resta bianca (non esiste ErrorBoundary).
 *
 * Il pin del digest riduce la finestra, non la elimina.
 */

/** Cosa fare quando un chunk non è recuperabile. */
export type RecoveryAction = 'reload' | 'notify';

export interface RecoveryContext {
  /** Rotta su cui si trovava l'utente quando il chunk è fallito. */
  pathname: string;
  /** Riferimento al chunk mancante, per il log. */
  chunkUrl: string;
}

const GUARD_KEY = 'chunk-recovery:last-reload';
const GUARD_WINDOW_MS = 10_000;

/**
 * Ricarica al massimo una volta ogni GUARD_WINDOW_MS.
 * Senza questo guard, una replica stabilmente incoerente manderebbe il
 * browser in reload loop infinito.
 */
function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(GUARD_KEY) ?? 0);
    if (Date.now() - last < GUARD_WINDOW_MS) return false;
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage non disponibile (private mode): procedi comunque.
  }
  window.location.reload();
  return true;
}

/**
 * Policy di recupero: ricarica sempre.
 *
 * La scelta alternativa era notificare l'utente invece di ricaricare, per non
 * perdere il lavoro in corso su DMAPage — dove 16 dei 23 chunk lazy sono dialog
 * aperti al click, quindi il fallimento arriva dopo che l'utente ha già inserito
 * qualcosa. È stata scartata: l'app non protegge i dati non salvati in nessun
 * altro punto (nessun beforeunload, useBlocker o isDirty in tutto src/), quindi
 * un reload accidentale o la chiusura del tab hanno già oggi lo stesso effetto.
 * Introdurre una rete di sicurezza solo qui sarebbe incoerente, e richiederebbe
 * uno Snackbar che non esiste ancora.
 *
 * Il reload preserva l'URL: l'utente torna dov'era, sulla build corrente.
 * Se in futuro l'app acquisisce un dirty-tracking vero, questa funzione può
 * restituire 'notify' per le rotte protette e installChunkRecovery accetta già
 * il callback corrispondente.
 */
export function defaultPolicy(ctx: RecoveryContext): RecoveryAction {
  void ctx;
  return 'reload';
}

/**
 * Installa l'handler globale. Chiamare una volta sola, prima del render.
 *
 * @param policy   decide reload vs notify in base al contesto.
 * @param onNotify invocato quando la policy sceglie 'notify' — cablalo allo
 *                 snackbar dell'app per offrire "Ricarica" all'utente.
 */
export function installChunkRecovery(
  policy: (ctx: RecoveryContext) => RecoveryAction = defaultPolicy,
  onNotify?: (ctx: RecoveryContext, reload: () => void) => void,
): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Impedisce che l'errore propaghi e sbianchi la pagina.
    event.preventDefault();

    const ctx: RecoveryContext = {
      pathname: window.location.pathname,
      chunkUrl:
        (event as unknown as { payload?: { message?: string } }).payload?.message ?? 'unknown',
    };

    console.warn('[chunkRecovery] chunk non caricabile', ctx);

    if (policy(ctx) === 'notify' && onNotify) {
      onNotify(ctx, () => reloadOnce());
      return;
    }
    reloadOnce();
  });
}
